import IService from '../IService';
import app from 'app';

export default IService.extend({
  delete: IService.synchronized(function ({ id, template }) {
    const promise = new $.Deferred();

    $.ajax({
      url: app.apiUrl(`/api/v1/draftStories/${id}`),
      type: 'DELETE',

      error: (jqXHR) => {
        if (jqXHR.status === 423) {
          // A version of the group is locked by another user; nothing was
          // deleted. Unlike the branches below (which reject with the dialog's
          // resolve value), reject with the error object so callers can branch
          // on errorCode and show contextual copy.
          promise.reject(this.createError(jqXHR));
        } else if (jqXHR.status === 404) {
          app.dialog
            .error({
              type: app.dialog.types.ERROR_1,
              title: template
                ? app.culture.storyboards.deleteError
                : app.culture.story.deleteError,
              showContactCsm: true,
            })
            .then(promise.reject);
        } else {
          app.dialog.serverError(this.createError(jqXHR)).then(promise.reject);
        }
      },

      dataType: 'json',
      success: (response) => {
        promise.resolve(response);
      },
    });

    return promise.promise();
  }),

  createAiStoryAiAPI: IService.synchronized(function (data, options) {
    const deferred = new $.Deferred();
    options = $.extend(
      {
        showError: true,
      },
      options
    );
    $.ajax({
      url: app.aiApiUrl('/api/video-generation-workflow'),
      type: 'POST',
      timeout: app.TIME.minute(5),
      contentType: 'application/json',
      data: JSON.stringify(data),
      error: (jqXHR, textStatus, errorThrown) => {
        if ('abort' === errorThrown) {
          deferred.reject(this.createError(jqXHR));
        } else if (options.showError) {
          app.dialog.serverError(this.createError(jqXHR)).then(deferred.reject);
        } else {
          deferred.reject(this.createError(jqXHR));
        }
      },
      success: (response) => {
        deferred.resolve(response);
      },
    });
    return deferred.promise();
  }),

  /**
   * Check the status of an AI task
   * @param {string} threadId - The thread ID to check
   * @returns {jQuery.Promise} Promise resolving to task status or error
   */
  checkAiTaskStatus: function (threadId) {
    const deferred = new $.Deferred();

    $.ajax({
      url: app.aiApiUrl(`/api/execution/${threadId}`),
      type: 'GET',
      error: (jqXHR, textStatus, errorThrown) => {
        if ('abort' === errorThrown) {
          return;
        }

        // Check if it's a 404 with JSON error response
        if (jqXHR.responseJSON) {
          // Resolve with error response so controller can retry
          deferred.resolve(jqXHR.responseJSON);
          return;
        }

        // For other errors (401, 403, 5xx, network), reject normally
        deferred.reject(this.createError(jqXHR));
      },
      dataType: 'json',
      success: (response) => {
        // Check if response contains an error (e.g., {"error": "Workflow not found"})
        if (response && response.error) {
          // Resolve with error response (not reject) so controller can handle retries
          deferred.resolve(response);
          return;
        }

        // Validate normal response format
        if (response && response.threadId && response.status) {
          deferred.resolve(response);
        } else {
          console.error('checkAiTaskStatus: Invalid response format', response);
          // Return as error response for controller to handle
          deferred.resolve({ error: 'Invalid response format' });
        }
      },
    });

    return deferred.promise();
  },

  createNewAiStory: IService.synchronized(function (data, options) {
    const deferred = new $.Deferred();
    options = $.extend(
      {
        showError: true,
      },
      options
    );

    $.ajax({
      url: app.apiUrl('/api/v1/aiStories'),
      type: 'POST',
      timeout: app.TIME.minute(5),

      contentType: 'application/json',
      data: JSON.stringify(data),

      error: (jqXHR, textStatus, errorThrown) => {
        if ('abort' === errorThrown) {
          deferred.reject(this.createError(jqXHR));
        } else if (options.showError) {
          app.dialog.serverError(this.createError(jqXHR)).then(deferred.reject);
        } else {
          deferred.reject(this.createError(jqXHR));
        }
      },
      success: (response) => {
        deferred.resolve(response);
      },
    });

    return deferred.promise();
  }),

  createNew: IService.synchronized(function (data, options) {
    const deferred = new $.Deferred();
    options = $.extend(
      {
        showError: true,
      },
      options
    );

    $.ajax({
      url: app.apiUrl('/api/v1/draftStories'),
      type: 'POST',
      timeout: app.TIME.minute(5),

      contentType: 'application/json',
      data: JSON.stringify(data),

      error: (jqXHR, textStatus, errorThrown) => {
        if ('abort' === errorThrown) {
          deferred.reject(this.createError(jqXHR));
        } else if (options.showError) {
          app.dialog.serverError(this.createError(jqXHR)).then(deferred.reject);
        } else {
          deferred.reject(this.createError(jqXHR));
        }
      },

      dataType: 'json',
      success: (response) => {
        if (_.isPlainObject(response) && !_.isEmpty(response.id)) {
          deferred.resolve(response);
        } else if (options.showError) {
          app.dialog.serverError(response).then(deferred.reject);
        } else {
          deferred.reject(response);
        }
      },
    });

    return deferred.promise();
  }),

  getStory: IService.synchronized(function (id) {
    const deferred = new $.Deferred();

    $.ajax({
      url: app.apiUrl(`/api/v1/draftStories/${id}`),
      type: 'GET',
      contentType: 'application/json',

      error: (jqXHR, textStatus, errorThrown) => {
        if ('abort' !== errorThrown) {
          deferred.reject(this.createError(jqXHR));
        }
      },

      dataType: 'json',
      success: function (response) {
        if (_.isPlainObject(response) && !_.isEmpty(response.story)) {
          deferred.resolve({
            story: response.story,
            externalStoryEntryId: response.externalStoryEntryId,
          });
        } else {
          deferred.reject(response);
        }
      },
    });

    return deferred.promise();
  }),

  /**
   * mark story as used by current user
   */
  lockON: IService.synchronized(function (storyId) {
    const deferred = new $.Deferred();

    $.ajax({
      url: app.apiUrl(`/api/v1/draftStories/${storyId}/lock`),
      type: 'PUT',

      error: (jqXHR, textStatus) => {
        if ('abort' !== textStatus) {
          deferred.reject(this.createError(jqXHR));
        }
      },

      success: () => {
        deferred.resolve();
      },
    });

    return deferred.promise();
  }),

  /**
   * mark story as free to use by other users
   */
  lockOFF: IService.synchronized(function (storyId) {
    const deferred = new $.Deferred();

    $.ajax({
      url: app.apiUrl(`/api/v1/draftStories/${storyId}/lock`),
      type: 'DELETE',

      error: (jqXHR, textStatus) => {
        if ('abort' !== textStatus) {
          deferred.reject(this.createError(jqXHR));
        }
      },

      success: () => {
        deferred.resolve();
      },
    });

    return deferred.promise();
  }),

  manualSave: IService.synchronized(function (data, options) {
    const deferred = new $.Deferred();
    options = $.extend(
      {
        showError: true,
      },
      options
    );

    console.log('\u26A1SAVE\u26A1');
    $.ajax({
      url: app.apiUrl(`/api/v1/draftStories/${data.id}`),
      type: 'PUT',
      timeout: app.TIME.minute(5),

      contentType: 'application/json',
      data: JSON.stringify(data),

      error: (jqXHR, textStatus, errorThrown) => {
        if (options.showError) {
          app.dialog.serverError(this.createError(jqXHR)).then(deferred.reject);
        } else {
          deferred.reject(this.createError(jqXHR));
        }
      },

      success: deferred.resolve,
    });

    return deferred.promise();
  }),

  changeStoryBrand: IService.synchronized(function ({
    storyId,
    targetProgrammerId,
    brandId,
    themeId,
  }) {
    return fetch(
      app.apiUrl(`/api/v1/draftStories/${storyId}/changeStoryBrand`),
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...app.rootStore.getters['user/xhrHeaders'],
        },
        body: JSON.stringify({
          programmerId: targetProgrammerId,
          brandId,
          themeId,
        }),
      }
    );
  }),

  disabledCustomOverlays: IService.synchronized(function ({
    storyId,
    targetProgrammerId,
    themeId,
  }) {
    if (!themeId) {
      app.doctorController.report(
        '// TODO: draftStories - disabledCustomOverlays - missing themeId'
      );
      return Promise.reject();
    }

    return new Promise((resolve, reject) => {
      fetch(
        app.apiUrl(`/api/v1/draftStories/${storyId}/disabledCustomOverlays`, {
          destProgrammerId: targetProgrammerId,
          destThemeId: themeId,
        }),
        {
          method: 'GET',
          credentials: 'include',
          headers: { ...app.rootStore.getters['user/xhrHeaders'] },
        }
      )
        .then((res) => {
          return res.ok ? res.json() : Promise.reject();
        })
        .then((json) => {
          if (Object.getOwnPropertyDescriptor(json, 'disabledCustomOverlays')) {
            resolve(json);
          } else {
            app.doctorController.report(
              `disabledCustomOverlays bad response: ${JSON.stringify(json)}`
            );
            reject();
          }
        })
        .catch(reject);
    });
  }),

  produce: IService.synchronized(function (storyId) {
    const rv = new $.Deferred();

    $.ajax({
      url: app.apiUrl(`/api/v1/draftStories/${storyId}/produce`),
      type: 'POST',
      timeout: app.TIME.minute(5),

      error: (jqXHR, textStatus) => {
        if ('abort' !== textStatus) {
          rv.reject(this.createError(jqXHR));
        }
      },

      success: () => {
        rv.resolve();
      },
    });

    return rv.promise();
  }),

  createDraftStoryFromDraftStory: IService.synchronized(function ({
    storyId,
    newTitle,
    destProgrammerId,
    storyStartPoint,
    brandId,
    themeId,
  }) {
    const deferred = new $.Deferred();

    console.log('\u26A1createDraftStoryFromDraftStory\u26A1');
    $.ajax({
      url: app.apiUrl(`/api/v1/draftStories/${storyId}`),
      type: 'POST',

      contentType: 'application/json',
      data: JSON.stringify({
        newTitle,
        destProgrammerId,
        storyStartPoint,
        destBrandId: brandId,
        destThemeId: themeId,
      }),

      error: (jqXHR, textStatus) => {
        if ('abort' === textStatus) {
          deferred.reject(this.createError(jqXHR));
        } else {
          app.dialog.serverError(this.createError(jqXHR)).then(deferred.reject);
        }
      },

      dataType: 'json',
      success: (res) => {
        deferred.resolve(res);
      },
    });

    return deferred.promise();
  }),

  /**
   * Get the version group ("folder") of a story.
   * @param {string} storyId - Any member of the group (root or version)
   * @returns {jQuery.Promise} Promise resolving to the version group payload
   */
  getVersions: function (storyId, options) {
    const deferred = new $.Deferred();
    options = $.extend(
      {
        showError: false,
      },
      options
    );

    $.ajax({
      url: app.apiUrl(`/api/v1/draftStories/${storyId}/versions`),
      type: 'GET',
      contentType: 'application/json',

      error: (jqXHR, textStatus, errorThrown) => {
        if ('abort' === errorThrown) {
          return;
        }
        if (options.showError) {
          app.dialog.serverError(this.createError(jqXHR)).then(deferred.reject);
        } else {
          deferred.reject(this.createError(jqXHR));
        }
      },

      dataType: 'json',
      success: (response) => {
        if (_.isPlainObject(response) && !_.isEmpty(response.versions)) {
          deferred.resolve(response);
        } else {
          deferred.reject(response);
        }
      },
    });

    return deferred.promise();
  },

  /**
   * Branch a version (and its descendants) out into a new independent group.
   * @param {string} storyId - The version to branch
   * @returns {jQuery.Promise} Promise resolving to { rootStoryId, newFolderId, movedStoryIds }
   */
  branch: IService.synchronized(function (storyId, options) {
    const deferred = new $.Deferred();
    options = $.extend(
      {
        showError: true,
      },
      options
    );

    $.ajax({
      url: app.apiUrl(`/api/v1/draftStories/${storyId}/branch`),
      type: 'POST',

      error: (jqXHR, textStatus, errorThrown) => {
        if ('abort' === errorThrown) {
          deferred.reject(this.createError(jqXHR));
        } else if (jqXHR.status === 400) {
          // Already a root/standalone - caller handles with a friendly message.
          deferred.reject(this.createError(jqXHR));
        } else if (options.showError) {
          app.dialog.serverError(this.createError(jqXHR)).then(deferred.reject);
        } else {
          deferred.reject(this.createError(jqXHR));
        }
      },

      dataType: 'json',
      success: (response) => {
        deferred.resolve(response);
      },
    });

    return deferred.promise();
  }),

  /**
   * Delete an entire version group ("folder") in one server call.
   * @param {string} folderId - The group key (original root story id)
   * @returns {jQuery.Promise} Promise resolving to { folderId, deletedStoryIds }
   */
  deleteFolder: IService.synchronized(function (folderId) {
    const promise = new $.Deferred();

    $.ajax({
      url: app.apiUrl(`/api/v1/draftStories/folders/${folderId}`),
      type: 'DELETE',

      error: (jqXHR) => {
        // 423 (a version of the group is being edited - by anyone, including
        // the requester; all-or-nothing, nothing was deleted) and 404 (group
        // already gone or dissolved) are handled contextually by the caller,
        // so reject with the error object and show no dialog here. Other
        // statuses fall back to the generic server-error dialog.
        if (jqXHR.status === 423 || jqXHR.status === 404) {
          promise.reject(this.createError(jqXHR));
        } else {
          app.dialog.serverError(this.createError(jqXHR)).then(promise.reject);
        }
      },

      dataType: 'json',
      success: (response) => {
        promise.resolve(response);
      },
    });

    return promise.promise();
  }),
});
